import React from 'react';
import {
	Box,
	Text,
	VStack,
	HStack,
	Avatar,
	Badge,
	Button,
	Icon,
	Spinner,
	Divider,
	Drawer,
	DrawerBody,
	DrawerHeader,
	DrawerOverlay,
	DrawerContent,
	DrawerCloseButton,
	Checkbox,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from '@chakra-ui/react';
import { FiX, FiClock } from 'react-icons/fi';
import useAppToast from 'components/toast';
import UserPicker, { userLabel } from 'components/userPicker/UserPicker';
import {
	useListAssignmentsQuery,
	useListAssignableUsersQuery,
	useAssignUserMutation,
	useRevokeUserMutation,
	useListAuditQuery,
} from 'api/metaWhatsappApi';
/**
 * ★ ONE FORMAT FOR A LIST OF DATES, WHICH `ago()` DELIBERATELY IS NOT.
 * The shared helper is relative under a day and absolute beyond it — right for
 * "last message received 5 min ago", wrong for a history list, where it makes
 * one row read "2 h ago" and the row under it "9/1/2026, 1:00:00 PM". An audit
 * trail wants dates it can scan and compare, so this formats every row the same
 * way and drops the seconds nobody reads.
 */
const when = (date) =>
	date
		? new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
		: 'an unknown date';

/**
 * Who may work one number — the whole of it, in one place.
 *
 * ★ NOTHING ABOUT THE META CONNECTION APPEARS HERE, AND THAT IS THE POINT.
 * Changing who answers a number is an operational act that happens weekly;
 * connecting the number to Meta happened once. Mixing them is what made people
 * believe a reassignment might need a reconnection — so this drawer cannot
 * connect, cannot re-authenticate, cannot touch the webhook, and cannot re-run
 * the send test. It assigns people.
 *
 * ★ REVOKING SOMEONE WHO IS HOLDING THREADS ASKS FIRST.
 * A revoke does not change `conversation.owner`, so removing an agent who holds
 * open conversations leaves them owned by someone who can no longer answer —
 * the successor may read them and every send is refused. The server can release
 * them in the same request; whether it should is a decision, so it is asked.
 */

/** One person currently on the number. */
function Assignee({ assignment, onRemove, isBusy }) {
	const user = assignment.user;
	const held = assignment.heldConversations ?? 0;

	return (
		<HStack spacing="3" px="3" py="2" borderRadius="md" bg="bg.subtle" justify="space-between">
			<HStack spacing="2.5" minW="0">
				<Avatar size="sm" name={userLabel(user)} src={user?.profileImage} />
				<Box minW="0">
					<HStack spacing="1.5">
						<Text fontSize="sm" color="text.primary" noOfLines={1}>
							{userLabel(user)}
						</Text>
						{/* Assigned is not the same as able to see it: a role can be
						    changed long after the assignment was made. */}
						{user?.canUseInbox === false && (
							<Badge colorScheme="yellow" variant="subtle" fontSize="0.55rem">
								No inbox access
							</Badge>
						)}
					</HStack>
					<Text fontSize="0.7rem" color="text.muted" noOfLines={1}>
						{user?.email}
						{held > 0 && ` · holding ${held} conversation${held === 1 ? '' : 's'}`}
					</Text>
				</Box>
			</HStack>
			<Button
				size="xs"
				variant="ghost"
				leftIcon={<Icon as={FiX} />}
				onClick={() => onRemove(assignment)}
				isDisabled={isBusy}
			>
				Remove
			</Button>
		</HStack>
	);
}

/** The grant history — the assignment collection is its own audit trail. */
function History({ phoneNumberId }) {
	const { data: entries = [], isLoading } = useListAuditQuery({ channel: phoneNumberId, limit: 50 });

	if (isLoading) return <Spinner size="xs" />;
	if (!entries.length) {
		return (
			<Text fontSize="xs" color="text.muted" fontStyle="italic">
				Nothing yet.
			</Text>
		);
	}

	return (
		<VStack align="stretch" spacing="1.5">
			{entries.map((e) => (
				<HStack key={e._id} spacing="2" align="start">
					<Icon as={FiClock} color="text.muted" boxSize="3" mt="0.5" />
					<Text fontSize="xs" color="text.secondary" lineHeight="1.6">
						<Box as="span" color="text.primary">
							{userLabel(e.user)}
						</Box>
						{e.revokedAt ? ' removed ' : ' added '}
						{when(e.revokedAt ?? e.assignedAt ?? e.createdAt)}
						{(e.revokedBy || e.assignedBy) && (
							<Box as="span" color="text.muted">
								{' by '}
								{userLabel(e.revokedAt ? e.revokedBy : e.assignedBy)}
							</Box>
						)}
						{e.revokeReason && e.revokeReason !== 'manual' && (
							<Box as="span" color="text.muted">{` (${e.revokeReason})`}</Box>
						)}
					</Text>
				</HStack>
			))}
		</VStack>
	);
}

export default function ManageDrawer({ channel, isOpen, onClose }) {
	const notify = useAppToast();
	const phoneNumberId = channel?.phoneNumberId;

	const { data: assignments = [], isLoading } = useListAssignmentsQuery(phoneNumberId, {
		skip: !phoneNumberId,
	});
	const [assignUser, { isLoading: assigning }] = useAssignUserMutation();
	const [revokeUser, { isLoading: revoking }] = useRevokeUserMutation();

	// The pending removal, held while we ask what to do with their threads.
	const [pending, setPending] = React.useState(null);
	const [alsoRelease, setAlsoRelease] = React.useState(true);

	const useSearchPeople = (q, page, isOpen2) => {
		const { data, isFetching } = useListAssignableUsersQuery(
			{ phoneNumberId, q, page },
			{ skip: !isOpen2 || !phoneNumberId },
		);
		return { users: data?.users ?? [], pagination: data?.pagination ?? null, isFetching };
	};

	const add = async (user) => {
		try {
			await assignUser({ phoneNumberId, userId: user._id }).unwrap();
			notify.success('Assigned', `${userLabel(user)} can now use this number.`);
		} catch (err) {
			notify.error('Not assigned', err?.data?.reason ?? 'That assignment could not be saved.');
		}
	};

	const askToRemove = (assignment) => {
		setAlsoRelease((assignment.heldConversations ?? 0) > 0);
		setPending(assignment);
	};

	const confirmRemove = async () => {
		const assignment = pending;
		const held = assignment.heldConversations ?? 0;
		try {
			const result = await revokeUser({
				phoneNumberId,
				userId: assignment.user?._id ?? assignment.user,
				reason: 'manual',
				releaseOwned: alsoRelease && held > 0,
			}).unwrap();

			notify.success(
				'Removed',
				result?.released
					? `${userLabel(assignment.user)} no longer sees this number. ${result.released} conversation${
							result.released === 1 ? '' : 's'
					  } released for someone else to pick up.`
					: `${userLabel(assignment.user)} no longer sees this number.`,
			);
		} catch (err) {
			notify.error('Not removed', 'That assignment could not be removed.');
		} finally {
			setPending(null);
		}
	};

	const held = pending?.heldConversations ?? 0;

	return (
		<>
			<Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
				<DrawerOverlay />
				<DrawerContent bg="bg.surface">
					<DrawerCloseButton />
					<DrawerHeader borderBottomWidth="1px" borderColor="border.default">
						<Text fontSize="md" color="text.primary">
							{channel?.label || channel?.displayPhoneNumber || phoneNumberId}
						</Text>
						<Text fontSize="xs" color="text.muted" fontWeight="normal" mt="0.5">
							Who may send and receive on this number
						</Text>
					</DrawerHeader>

					<DrawerBody>
						<VStack align="stretch" spacing="5" py="2">
							<Box>
								<Text fontSize="sm" color="text.primary" mb="2">
									On this number
								</Text>
								{isLoading ? (
									<Spinner size="xs" />
								) : assignments.length === 0 ? (
									<Text fontSize="xs" color="warning" fontStyle="italic" mb="3">
										Nobody yet — this number&apos;s conversations are invisible until someone is
										assigned, and it cannot send.
									</Text>
								) : (
									<VStack align="stretch" spacing="1.5" mb="3">
										{assignments.map((a) => (
											<Assignee
												key={a._id}
												assignment={a}
												onRemove={askToRemove}
												isBusy={revoking}
											/>
										))}
									</VStack>
								)}

								<UserPicker
									useSearch={useSearchPeople}
									onSelect={add}
									isBusy={assigning}
									placeholder="Add a person by name or email…"
									exhaustedText="Everyone is already assigned."
									renderMeta={(u) =>
										u.canUseInbox === false ? (
											<Badge colorScheme="yellow" variant="subtle" fontSize="0.55rem">
												No inbox access
											</Badge>
										) : null
									}
									renderHint={(u) =>
										u.canUseInbox === false
											? 'Needs the WhatsApp module on their role'
											: u.email
									}
								/>
							</Box>

							<Divider borderColor="border.subtle" />

							<Box>
								<Text fontSize="sm" color="text.primary" mb="1">
									History
								</Text>
								<Text fontSize="xs" color="text.muted" mb="2">
									Every grant is kept, including removed ones.
								</Text>
								<History phoneNumberId={phoneNumberId} />
							</Box>
						</VStack>
					</DrawerBody>
				</DrawerContent>
			</Drawer>

			{/* ★ ASKED, NOT ASSUMED. Releasing is usually right when handing a number
			    over and wrong when someone is merely changing teams, and only the
			    administrator knows which this is. */}
			<Modal isOpen={Boolean(pending)} onClose={() => setPending(null)} isCentered size="md">
				<ModalOverlay />
				<ModalContent bg="bg.surface">
					<ModalHeader fontSize="md" color="text.primary">
						Remove {userLabel(pending?.user)}?
					</ModalHeader>
					<ModalBody>
						<Text fontSize="sm" color="text.secondary" lineHeight="1.7">
							They will immediately stop seeing this number&apos;s conversations.
						</Text>
						{held > 0 && (
							<Box mt="3" p="3" bg="bg.subtle" borderRadius="md">
								<Text fontSize="sm" color="text.primary" mb="2">
									They are holding {held} open conversation{held === 1 ? '' : 's'}.
								</Text>
								<Checkbox
									isChecked={alsoRelease}
									onChange={(e) => setAlsoRelease(e.target.checked)}
									size="sm"
								>
									<Text fontSize="xs" color="text.secondary">
										Release {held === 1 ? 'it' : 'them'} so someone else can pick{' '}
										{held === 1 ? 'it' : 'them'} up
									</Text>
								</Checkbox>
								<Text fontSize="0.7rem" color="text.muted" mt="2" lineHeight="1.6">
									{alsoRelease
										? 'Released conversations stay visible to everyone with access and can be claimed by whoever answers next.'
										: 'Left as they are, nobody will be able to reply to them until an administrator transfers each one.'}
								</Text>
							</Box>
						)}
					</ModalBody>
					<ModalFooter>
						<Button size="sm" variant="ghost" mr="2" onClick={() => setPending(null)}>
							Cancel
						</Button>
						<Button size="sm" colorScheme="red" onClick={confirmRemove} isLoading={revoking}>
							Remove
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
}
